import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Repeat2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { publishingApiClient } from '../../api/http/publishingApiClient'
import type { WireArticle } from '../../api/types'
import type { WorkflowHistoryEntry } from '../metrics/types'

interface QualityChurnWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

type ChurnRow = {
  articleId: string
  title: string
  churnScore: number
  transitions: number
  reentries: number
  bouncebacks: number
  blockedEvents: number
}

const STAGE_ORDER: Record<string, number> = {
  qc_review: 1,
  layout_review: 2,
  editorial_approval: 3,
  scheduled: 4,
  post_pub_review: 5,
  published: 6,
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      fgSecondary: '#6b7280',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

function computeChurn(articleId: string, title: string, events: WorkflowHistoryEntry[]): ChurnRow {
  const ordered = events
    .slice()
    .sort((a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime())

  const stageCounts = new Map<string, number>()
  let bouncebacks = 0
  let blockedEvents = 0

  for (let i = 0; i < ordered.length; i += 1) {
    const event = ordered[i]
    stageCounts.set(event.stage, (stageCounts.get(event.stage) ?? 0) + 1)
    if (event.stage === 'blocked') blockedEvents += 1

    const prev = ordered[i - 1]
    if (!prev) continue
    const prevRank = STAGE_ORDER[prev.stage]
    const currentRank = STAGE_ORDER[event.stage]
    if (prevRank && currentRank && currentRank < prevRank) {
      bouncebacks += 1
    }
  }

  const reentries = Array.from(stageCounts.values()).reduce((sum, count) => sum + Math.max(0, count - 1), 0)
  const transitions = ordered.length
  const churnScore = reentries * 2 + bouncebacks * 3 + blockedEvents * 2

  return {
    articleId,
    title,
    churnScore,
    transitions,
    reentries,
    bouncebacks,
    blockedEvents,
  }
}

function useQualityChurn(articles: WireArticle[]) {
  const capped = useMemo(() => articles.slice(0, 24), [articles])
  const stableKey = useMemo(() => capped.map((article) => article.id).join(','), [capped])

  return useQuery({
    queryKey: ['dashboard', 'quality-churn', stableKey],
    queryFn: async () => {
      const results = await Promise.allSettled(
        capped.map(async (article) => {
          const { data } = await publishingApiClient.get<WorkflowHistoryEntry[]>(`/drafts/${article.id}/workflow-history`)
          return computeChurn(article.id, article.title, data ?? [])
        }),
      )

      return results
        .filter((result): result is PromiseFulfilledResult<ChurnRow> => result.status === 'fulfilled')
        .map((result) => result.value)
        .sort((a, b) => b.churnScore - a.churnScore || b.transitions - a.transitions)
    },
    enabled: capped.length > 0,
    staleTime: 60_000,
  })
}

export function QualityChurnWidget({ articles, embedded = false }: QualityChurnWidgetProps) {
  const colors = useChartTheme()
  const { data = [], isPending } = useQualityChurn(articles)

  const topRows = useMemo(() => data.filter((row) => row.churnScore > 0).slice(0, 6), [data])

  const summary = useMemo(() => {
    const analyzed = data.length
    const withChurn = data.filter((row) => row.churnScore > 0).length
    const avgTransitions = analyzed > 0
      ? data.reduce((sum, row) => sum + row.transitions, 0) / analyzed
      : 0
    const totalBouncebacks = data.reduce((sum, row) => sum + row.bouncebacks, 0)
    return {
      analyzed,
      withChurn,
      avgTransitions,
      totalBouncebacks,
    }
  }, [data])

  const chartOption = useMemo(() => {
    if (topRows.length === 0) return null
    return {
      grid: { top: 8, right: 16, bottom: 8, left: 8, containLabel: true },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        formatter: (params: { dataIndex?: number }[]) => {
          const idx = params[0]?.dataIndex ?? 0
          const row = topRows[idx]
          if (!row) return ''
          return [
            `<b style="font-size:12px">${row.title}</b>`,
            `Churn score: ${row.churnScore}`,
            `Transitions: ${row.transitions}`,
            `Re-entries: ${row.reentries}`,
            `Bouncebacks: ${row.bouncebacks}`,
            `Blocked events: ${row.blockedEvents}`,
          ].join('<br/>')
        },
      },
      xAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600 },
        splitLine: { lineStyle: { color: colors.line } },
      },
      yAxis: {
        type: 'category' as const,
        inverse: true,
        data: topRows.map((row) => truncate(row.title, 32)),
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600, width: 140, overflow: 'truncate' as const },
        axisLine: { lineStyle: { color: colors.line } },
      },
      series: [
        {
          name: 'Churn score',
          type: 'bar' as const,
          data: topRows.map((row) => row.churnScore),
          barMaxWidth: 16,
          itemStyle: { color: '#f97316', borderRadius: [0, 4, 4, 0] },
        },
      ],
    }
  }, [topRows, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {isPending ? (
        <div className="space-y-2 rounded-xl bg-canvas px-4 py-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-fill" />)}
        </div>
      ) : chartOption ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts option={chartOption} style={{ height: Math.max(180, topRows.length * 38) }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No churn signals detected in the analyzed drafts.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {summary.analyzed > 0
          ? `${summary.withChurn}/${summary.analyzed} drafts show churn. Avg transitions: ${summary.avgTransitions.toFixed(1)}. Bouncebacks: ${summary.totalBouncebacks}.`
          : 'No draft history available for churn analysis yet.'}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Repeat2 size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Quality Churn</span>
      </div>
      {body}
    </section>
  )
}
